import React, { useState, useEffect, useRef, useCallback } from "react";
import { Chrome } from "@uiw/react-color";
import { MDBBtn, MDBIcon, MDBSpinner } from "mdb-react-ui-kit";
import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import "./AdminColorPalette.css";

const APP_CODE = "TEAN";

const DEFAULTS = {
  primario:  "#D2B48C",
  texto:     "#3C2F2F",
  acento:    "#8B0000",
  destacado: "#8B6914",
  tarjeta:   "#FAF5EE",
};

const CSS_VARS = {
  primario:  "--color-primario",
  texto:     "--color-texto",
  acento:    "--color-acento",
  destacado: "--color-destacado",
  tarjeta:   "--color-tarjeta",
};

const CONTENT_KEYS = {
  primario:  "THEME_COLOR_PRIMARIO",
  texto:     "THEME_COLOR_TEXTO",
  acento:    "THEME_COLOR_ACENTO",
  destacado: "THEME_COLOR_DESTACADO",
  tarjeta:   "THEME_COLOR_TARJETA",
};

const LABELS = {
  primario:  "Fondo principal",
  texto:     "Texto",
  acento:    "Acento / CTAs",
  destacado: "Destacado / Bordes",
  tarjeta:   "Tarjetas",
};

function readCurrentColors() {
  const style = getComputedStyle(document.documentElement);
  return Object.fromEntries(
    Object.entries(CSS_VARS).map(([key, cssVar]) => [
      key,
      style.getPropertyValue(cssVar).trim() || DEFAULTS[key],
    ])
  );
}

export default function AdminColorPalette() {
  const [open, setOpen] = useState(false);
  const [colors, setColors] = useState(DEFAULTS);
  const [activePicker, setActivePicker] = useState(null);
  const [saving, setSaving] = useState(false);
  const panelRef = useRef(null);

  // Al abrir el panel, leer los colores actuales del DOM
  useEffect(() => {
    if (open) {
      setColors(readCurrentColors());
    }
  }, [open]);

  // Cerrar picker al hacer clic fuera del panel
  useEffect(() => {
    if (!activePicker) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setActivePicker(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [activePicker]);

  const applyColor = (key, hex) => {
    document.documentElement.style.setProperty(CSS_VARS[key], hex);
    setColors((prev) => ({ ...prev, [key]: hex }));
  };

  const handleHexInput = (key, value) => {
    // Actualiza el estado del input siempre (permite escribir libremente)
    setColors((prev) => ({ ...prev, [key]: value }));
    // Aplica al DOM solo cuando el valor es un hex válido (#RGB o #RRGGBB)
    if (/^#[0-9a-fA-F]{3}$/.test(value) || /^#[0-9a-fA-F]{6}$/.test(value)) {
      document.documentElement.style.setProperty(CSS_VARS[key], value);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setActivePicker(null);
    const items = Object.entries(CONTENT_KEYS).map(([key, contentKey]) => ({
      contentKey,
      contentValue: colors[key],
      contentType: "color",
    }));
    try {
      const res = await doFetchJson(APP_CODE, "upsertSiteContentBatch", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(items),
      });
      if (res?.STATE === "SUCCESS") {
        toast.show({ message: "Paleta guardada correctamente", type: "success", position: "bottom-center", size: "wide", animation: "slide", timeout: 3000 });
      } else {
        toast.show({ message: res?.MESSAGE || "Error al guardar la paleta", type: "danger", position: "bottom-center", size: "wide", animation: "slide", timeout: 4000 });
      }
    } catch {
      toast.show({ message: "Error al guardar la paleta", type: "danger", position: "bottom-center", size: "wide", animation: "slide", timeout: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = () => {
    Object.entries(DEFAULTS).forEach(([key, val]) => {
      document.documentElement.style.setProperty(CSS_VARS[key], val);
    });
    setColors({ ...DEFAULTS });
    setActivePicker(null);
  };

  const handleToggle = useCallback(() => {
    setOpen((o) => !o);
    setActivePicker(null);
  }, []);

  return (
    <div className="acp-wrapper" ref={panelRef}>
      <button
        className={`acp-toggle-btn${open ? " acp-toggle-btn--active" : ""}`}
        title="Paleta de colores"
        onClick={handleToggle}
      >
        <MDBIcon fas icon="palette" />
      </button>

      {open && (
        <div className="acp-panel">
          <div className="acp-header">
            <span className="acp-title">
              <MDBIcon fas icon="palette" className="me-2" />
              Colores de la app
            </span>
            <button className="acp-close" onClick={() => setOpen(false)}>
              <MDBIcon fas icon="times" />
            </button>
          </div>

          <div className="acp-colors">
            {Object.keys(LABELS).map((key) => (
              <div key={key} className="acp-color-row">
                <button
                  className={`acp-swatch${activePicker === key ? " acp-swatch--active" : ""}`}
                  style={{ background: colors[key] }}
                  onClick={() => setActivePicker(activePicker === key ? null : key)}
                  title={`${LABELS[key]}: ${colors[key]}`}
                />
                <span className="acp-label">{LABELS[key]}</span>
                <input
                  className="acp-hex-input"
                  type="text"
                  value={colors[key]}
                  onChange={(e) => handleHexInput(key, e.target.value)}
                  maxLength={7}
                  spellCheck={false}
                />
              </div>
            ))}
          </div>

          {activePicker && (
            <div className="acp-picker-container">
              <Chrome
                color={colors[activePicker]}
                onChange={({ hex }) => applyColor(activePicker, hex)}
                disableAlpha
                style={{ width: "100%" }}
              />
            </div>
          )}

          <div className="acp-actions">
            <MDBBtn
              size="sm"
              color="light"
              onClick={handleRestore}
              disabled={saving}
            >
              Restaurar
            </MDBBtn>
            <MDBBtn
              size="sm"
              color="primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <MDBSpinner size="sm" className="me-1" /> : null}
              {saving ? "Guardando…" : "Guardar paleta"}
            </MDBBtn>
          </div>
        </div>
      )}
    </div>
  );
}
